/**
 * メトリクス画面用の日付ユーティリティ関数
 */

import dayjs, { Dayjs } from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'

dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)

/**
 * 日付範囲の単一アイテム
 */
export interface DateRangeItem {
  /** YYYY-MM-DD形式の日付文字列 */
  dateStr: string
  /** MM/DD形式の表示用日付 */
  displayDate: string
}

/**
 * 開始日から終了日までの日付配列を生成
 * @param startDate 開始日
 * @param endDate 終了日
 * @returns 日付範囲の配列
 */
export const generateDateRangeArray = (
  startDate: Dayjs,
  endDate: Dayjs
): DateRangeItem[] => {
  const result: DateRangeItem[] = []
  let currentDate = startDate

  while (currentDate.isSameOrBefore(endDate, 'day')) {
    result.push({
      dateStr: currentDate.format('YYYY-MM-DD'),
      displayDate: currentDate.format('MM/DD'),
    })
    currentDate = currentDate.add(1, 'day')
  }

  return result
}

/**
 * トレンド描画用の日付軸を生成
 *
 * daily は暦日で埋める (ゼロ埋め)。weekly/monthly は back が TruncWeek/TruncMonth の
 * バケット開始日を返すため、暦日軸に載せると期間開始前に食い込む先頭バケット
 * (例: 6/19開始の月次の '06-01') が落ちる。実データの日付をそのまま軸にする
 */
export const generateTrendAxis = (
  period: 'daily' | 'weekly' | 'monthly',
  startDate: Dayjs,
  endDate: Dayjs,
  dataDates: string[]
): DateRangeItem[] => {
  if (period === 'daily') {
    return generateDateRangeArray(startDate, endDate)
  }
  return Array.from(new Set(dataDates))
    .sort()
    .map((dateStr) => ({
      dateStr,
      displayDate: dayjs(dateStr).format('MM/DD'),
    }))
}

/**
 * データを日付範囲にマッピング（汎用版）
 * @param dateRange 日付範囲の配列
 * @param data データ配列
 * @param valueExtractor データから値を抽出する関数
 * @param defaultValue デフォルト値（データがない場合）
 * @returns マッピングされた結果
 */
export const mapDataToDateRange = <T extends { date: string }>(
  dateRange: DateRangeItem[],
  data: T[] | undefined,
  valueExtractor: (item: T) => number,
  defaultValue = 0
): Array<{ 日付: string; value: number }> => {
  // データを高速アクセス用のMapに変換
  const dataMap = new Map<string, number>()

  data?.forEach((item) => {
    dataMap.set(item.date, valueExtractor(item))
  })

  // 日付範囲にデータをマッピング
  return dateRange.map(({ dateStr, displayDate }) => ({
    日付: displayDate,
    value: dataMap.get(dateStr) ?? defaultValue,
  }))
}
